import { call, put, takeLatest } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';

import {
  productVersionsFetchSuccess,
  updateProductWhitelistSuccess,
  createProductVersionSuccess,
  updateProductAssetsSuccess,
  updateProductActiveSuccess,
  fetchProductVersions,
  productFeaturesFetchSuccess,
  fetchSeatFeatures,
  seatFeaturesFetchSuccess,
} from '../actions';
import { sendAction } from '../api';
import {
  SUPER_ADMIN_FETCH_PRODUCT_VERSIONS,
  SUPER_ADMIN_UPDATE_PRODUCT_WHITELIST,
  SUPER_ADMIN_CREATE_PRODUCT_VERSION,
  SUPER_ADMIN_UPDATE_PRODUCT_ASSETS,
  SUPER_ADMIN_UPDATE_PRODUCT_IS_ACTIVE,
  SUPER_ADMIN_FETCH_PRODUCT_FEATURES,
  SUPER_ADMIN_FETCH_SEAT_FEATURES,
  SUPER_ADMIN_SET_SEAT_FEATURE,
} from '../types';

// Minimal shape of the superadmin API config passed through to `sendAction`.
interface SuperClientConfig {
  baseApiUrl: string;
  getAuthToken: () => string;
  timeout?: number;
}

// Wrapped response returned by `sendAction`; `data` is untyped JSON from the API.
interface ProductResponse {
  succeeded?: boolean;
  data?: {
    products?: unknown[];
    product?: unknown;
  };
}

// Wrapped response for the product-features fetch; `data` is untyped feature JSON.
interface FeaturesResponse {
  succeeded?: boolean;
  data?: unknown;
}

// Action dispatched to update a product's whitelisted countries. Only the fields
// read by the saga are declared (the dispatched action also carries a `type`).
interface UpdateProductWhitelistAction {
  type: string;
  productId: string;
  addCountries: string[];
  removeCountries: string[];
}

// Action dispatched to create a new product version.
interface CreateProductVersionAction {
  type: string;
  name: string;
  productVersion: string;
  region: string;
  productType: string;
  releaseDate: string;
  whitelistedCountries: string[];
  active: boolean;
}

// Action dispatched to update a product's assets.
interface UpdateProductAssetsAction {
  type: string;
  productId: string;
  assets: Record<string, unknown>;
}

// Action dispatched to toggle a product's active status.
interface UpdateProductActiveAction {
  type: string;
  productId: string;
  active: boolean;
}

// Action dispatched to fetch a product's features.
interface FetchProductFeaturesAction {
  type: string;
  productName: string;
}

interface FetchSeatFeaturesAction {
  type: string;
  costCenterId: string;
  productAccessId: string;
}

interface SetSeatFeatureAction {
  type: string;
  costCenterId: string;
  productAccessId: string;
  userIds: string[];
  featureName: string;
  value: boolean | string | number;
  enabled: boolean | null;
}

// Fetch product versions
export function* fetchProductVersionsSaga(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  try {
    const action = CloudActions.adminGetProductAndVersionList();
    const response = (yield call(sendAction, config, action)) as ProductResponse;

    if (response && response.succeeded && response.data && response.data.products) {
      yield put(productVersionsFetchSuccess(response.data.products));
    } else {
      console.error('Failed to fetch product versions:', response);
      yield put(productVersionsFetchSuccess([]));
    }
  } catch (error) {
    console.error('Error fetching product versions:', error);
    yield put(productVersionsFetchSuccess([]));
  }
}

// Update product whitelist
export function* updateProductWhitelistSaga(
  config: SuperClientConfig,
  action: UpdateProductWhitelistAction,
): Generator<unknown, void, unknown> {
  try {
    const { productId, addCountries, removeCountries } = action;
    const cloudAction = CloudActions.adminUpdateProductWhitelistedCountries(
      productId,
      addCountries,
      removeCountries,
    );
    const response = (yield call(sendAction, config, cloudAction)) as ProductResponse;

    if (response && response.succeeded) {
      yield put(updateProductWhitelistSuccess());
      // Refetch product versions after successful update
      yield put(fetchProductVersions());
    } else {
      console.error('Failed to update product whitelist:', response);
    }
  } catch (error) {
    console.error('Error updating product whitelist:', error);
  }
}

// Create product version
export function* createProductVersionSaga(
  config: SuperClientConfig,
  action: CreateProductVersionAction,
): Generator<unknown, void, unknown> {
  try {
    const { name, productVersion, region, productType, releaseDate, whitelistedCountries, active } =
      action;
    const cloudAction = CloudActions.adminCreateProductVersion(
      name,
      productVersion,
      region,
      productType,
      releaseDate,
      whitelistedCountries,
      active,
    );
    const response = (yield call(sendAction, config, cloudAction)) as ProductResponse;

    if (response && response.succeeded && response.data && response.data.product) {
      yield put(createProductVersionSuccess(response.data.product));
      // Refetch product versions after successful creation
      yield put(fetchProductVersions());
    } else {
      console.error('Failed to create product version:', response);
    }
  } catch (error) {
    console.error('Error creating product version:', error);
  }
}

export function* watchFetchProductVersions(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_FETCH_PRODUCT_VERSIONS, fetchProductVersionsSaga, config);
}

export function* watchUpdateProductWhitelist(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_UPDATE_PRODUCT_WHITELIST, updateProductWhitelistSaga, config);
}

export function* watchCreateProductVersion(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_CREATE_PRODUCT_VERSION, createProductVersionSaga, config);
}

// Update product assets
export function* updateProductAssetsSaga(
  config: SuperClientConfig,
  action: UpdateProductAssetsAction,
): Generator<unknown, void, unknown> {
  try {
    const { productId, assets } = action;
    const cloudAction = CloudActions.adminUpdateProductAssets(productId, assets);
    const response = (yield call(sendAction, config, cloudAction)) as ProductResponse;

    if (response && response.succeeded && response.data && response.data.product) {
      yield put(updateProductAssetsSuccess(response.data.product));
      // Refetch product versions after successful update
      yield put(fetchProductVersions());
    } else {
      console.error('Failed to update product assets:', response);
    }
  } catch (error) {
    console.error('Error updating product assets:', error);
  }
}

export function* watchUpdateProductAssets(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_UPDATE_PRODUCT_ASSETS, updateProductAssetsSaga, config);
}

// Update product active status
export function* updateProductActiveSaga(
  config: SuperClientConfig,
  action: UpdateProductActiveAction,
): Generator<unknown, void, unknown> {
  try {
    const { productId, active } = action;
    const cloudAction = CloudActions.adminUpdateProductActive(productId, active);
    const response = (yield call(sendAction, config, cloudAction)) as ProductResponse;

    if (response && response.succeeded && response.data && response.data.product) {
      yield put(updateProductActiveSuccess(response.data.product));
      // Refetch product versions after successful update
      yield put(fetchProductVersions());
    } else {
      console.error('Failed to update product active status:', response);
    }
  } catch (error) {
    console.error('Error updating product active status:', error);
  }
}

export function* watchUpdateProductActive(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_UPDATE_PRODUCT_IS_ACTIVE, updateProductActiveSaga, config);
}

// Fetch product features
export function* fetchProductFeaturesSaga(
  config: SuperClientConfig,
  action: FetchProductFeaturesAction,
): Generator<unknown, void, unknown> {
  const { productName } = action;
  try {
    const cloudAction = CloudActions.adminGetProductFeatures(productName);
    const response = (yield call(sendAction, config, cloudAction)) as FeaturesResponse;

    if (response && response.succeeded && response.data) {
      yield put(productFeaturesFetchSuccess(productName, response.data));
    } else {
      console.error('Failed to fetch product features:', response);
      yield put(productFeaturesFetchSuccess(productName, []));
    }
  } catch (error) {
    console.error('Error fetching product features:', error);
    yield put(productFeaturesFetchSuccess(productName, []));
  }
}

/**
 * Reads every per-seat override on a product access. The backend action is superadmin-gated,
 * so a caller without the sudo claim gets a 401 - which surfaces here as an empty list rather
 * than an error, since the UI simply hides the controls in that case.
 */
export function* fetchSeatFeaturesSaga(
  config: SuperClientConfig,
  action: FetchSeatFeaturesAction,
): Generator<unknown, void, unknown> {
  const { costCenterId, productAccessId } = action;
  try {
    const cloudAction = CloudActions.adminGetFeaturesForProductAccess(costCenterId, productAccessId);
    const response = (yield call(sendAction, config, cloudAction)) as FeaturesResponse;

    if (response && response.succeeded && response.data) {
      yield put(seatFeaturesFetchSuccess(costCenterId, productAccessId, response.data));
    } else {
      yield put(seatFeaturesFetchSuccess(costCenterId, productAccessId, []));
    }
  } catch (error) {
    console.error('Error fetching seat features:', error);
    yield put(seatFeaturesFetchSuccess(costCenterId, productAccessId, []));
  }
}

export function* watchFetchSeatFeatures(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_FETCH_SEAT_FEATURES, fetchSeatFeaturesSaga, config);
}

/**
 * Applies one feature to every selected seat in a single batch.
 *
 * `enabled: null` means "clear the override" and maps to the remove action - materially
 * different from `false`, which records an explicit off.
 */
export function* setSeatFeatureSaga(
  config: SuperClientConfig,
  action: SetSeatFeatureAction,
): Generator<unknown, void, unknown> {
  const { costCenterId, productAccessId, userIds, featureName, value, enabled } = action;
  try {
    const actions = userIds.map((userId) =>
      enabled === null
        ? CloudActions.adminRemoveFeatureFromProductAccessUser(
            featureName,
            costCenterId,
            productAccessId,
            userId,
          )
        : CloudActions.adminAddFeatureToProductAccessUser(
            featureName,
            costCenterId,
            productAccessId,
            userId,
            value,
            enabled,
          ),
    );

    yield call(sendAction, config, CloudActions.adminBatchActions(actions));
  } catch (error) {
    console.error('Error setting seat feature:', error);
  }
  // Refetch either way so the UI reflects whatever actually landed.
  yield put(fetchSeatFeatures(costCenterId, productAccessId));
}

export function* watchSetSeatFeature(config: SuperClientConfig): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_SET_SEAT_FEATURE, setSeatFeatureSaga, config);
}

export function* watchFetchProductFeatures(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_FETCH_PRODUCT_FEATURES, fetchProductFeaturesSaga, config);
}
